import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import ReactGA from 'react-ga4';
import axios from 'axios';
import './NewsCustom.css';

const News = () => {
    const [newsItems, setNewsItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [feedInfo, setFeedInfo] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        try { ReactGA.send({ hitType: 'pageview', page: '/news' }); } catch { }

        // Fetch news from API
        const fetchNews = async () => {
            try {
                setIsLoading(true);
                setError('');

                const response = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/assetmgmt/newsletter/news?limit=20`
                );

                if (response.data.code === 200 && response.data.data) {
                    const { items, feedTitle, feedDescription, lastBuildDate, itemCount } = response.data.data;

                    // Deduplicate news items based on guid, link, or title
                    const uniqueItems = [];
                    const seenIdentifiers = new Set();

                    (items || []).forEach(item => {
                        // Create a unique identifier using guid (preferred), link, or title
                        const identifier = item.guid || item.link || item.title;

                        if (identifier && !seenIdentifiers.has(identifier)) {
                            seenIdentifiers.add(identifier);
                            uniqueItems.push(item);
                        }
                    });

                    setNewsItems(uniqueItems);
                    setFeedInfo({
                        title: feedTitle,
                        description: feedDescription,
                        lastBuildDate,
                        itemCount
                    });
                } else {
                    setError('No news articles available at the moment.');
                }
            } catch (err) {
                console.error("Failed to fetch news:", err);
                if (err.response?.status === 409) {
                    setError('News feed is temporarily unavailable. Please try again later.');
                } else {
                    setError('Failed to load news. Please try again later.');
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchNews();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const stripHtml = (html) => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    return (
        <div className="min-h-screen bg-deep-charcoal text-white overflow-hidden relative selection:bg-emerald selection:text-white">
            {/* Background Ambience */}
            <div className="absolute top-0 left-0 w-full h-[50vh] bg-gradient-to-b from-emerald/10 to-transparent pointer-events-none" />
            <div className="absolute -top-[20%] -right-[10%] w-[600px] h-[600px] bg-electric-lime/5 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute top-[40%] -left-[10%] w-[400px] h-[400px] bg-emerald/10 rounded-full blur-[100px] pointer-events-none" />

            <div className="container mx-auto px-6 pt-32 pb-20 relative z-10">
                {/* Header Section */}
                <div className="text-center mb-20">
                    <motion.h1
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="text-5xl md:text-7xl font-black mb-6 tracking-tight uppercase bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-gray-400"
                    >
                        Latest <span className="text-electric-lime">News</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                        className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto"
                    >
                        Stay updated with the latest sports intelligence news and updates
                    </motion.p>
                    {feedInfo?.lastBuildDate && (
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5, duration: 0.8 }}
                            className="text-sm text-gray-500 mt-4"
                        >
                            Last updated: {formatDate(feedInfo.lastBuildDate)}
                        </motion.p>
                    )}
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="w-16 h-16 border-4 border-emerald/30 border-t-emerald rounded-full animate-spin mb-4"></div>
                        <p className="text-gray-400 text-lg">Loading news...</p>
                    </div>
                )}

                {/* Error State */}
                {error && !isLoading && (
                    <div className="max-w-2xl mx-auto">
                        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8 text-center">
                            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h3 className="text-2xl font-bold text-white mb-2">Unable to Load News</h3>
                            <p className="text-gray-400">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="mt-6 px-6 py-3 bg-emerald hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                )}

                {/* News Grid */}
                {!isLoading && !error && newsItems.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {newsItems.map((item, index) => (
                            <motion.div
                                key={item.guid || item.id || index}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 + (index * 0.05) }}
                                whileHover={{ y: -10 }}
                                className="group relative bg-surface-gray border border-white/5 rounded-2xl overflow-hidden shadow-2xl hover:shadow-glow-green/20 transition-all duration-500 flex flex-col"
                            >
                                {/* Image Banner */}
                                {item.thumbnail && (
                                    <div className="h-48 overflow-hidden relative">
                                        <div className="absolute inset-0 bg-gradient-to-t from-surface-gray to-transparent z-10 opacity-60" />
                                        <img
                                            src={item.thumbnail}
                                            alt={item.title}
                                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                        {item.category && (
                                            <div className="absolute top-4 left-4 z-20 bg-black/40 backdrop-blur-md px-3 py-1 rounded-full border border-white/10">
                                                <span className="text-xs font-bold text-electric-lime uppercase tracking-wider">{item.category}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Content */}
                                <div className="p-6 relative z-20 flex flex-col flex-grow">
                                    <div className="flex items-center gap-2 mb-4 text-xs text-gray-500 font-medium flex-wrap">
                                        {item.pubDate && (
                                            <span className="flex items-center gap-1">
                                                <svg className="w-3 h-3 text-emerald" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 002 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM9 14H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm-8 4H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2z" />
                                                </svg>
                                                {formatDate(item.pubDate)}
                                            </span>
                                        )}
                                        {item.author && (
                                            <span className="flex items-center gap-1">
                                                <svg className="w-3 h-3 text-emerald" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                                </svg>
                                                {item.author}
                                            </span>
                                        )}
                                    </div>

                                    <h2 className="text-xl font-bold mb-3 text-white group-hover:text-emerald transition-colors duration-300 line-clamp-2">
                                        {item.title}
                                    </h2>

                                    <p className="text-gray-400 text-sm mb-6 line-clamp-3 flex-grow">
                                        {stripHtml(item.description)}
                                    </p>

                                    {/* Footer / Action */}
                                    <div className="flex items-center justify-end border-t border-white/10 pt-4 mt-auto">
                                        <a
                                            href={item.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 text-sm font-semibold text-emerald hover:text-electric-lime transition-colors"
                                        >
                                            Read Article
                                            <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                                            </svg>
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Empty State */}
                {!isLoading && !error && newsItems.length === 0 && (
                    <div className="text-center py-20">
                        <svg className="w-24 h-24 text-gray-600 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                        </svg>
                        <h3 className="text-2xl text-gray-500 font-semibold mb-2">No News Available</h3>
                        <p className="text-gray-600">Check back soon for the latest updates</p>
                    </div>
                )}

                {/* Footer Note */}
                {!isLoading && !error && newsItems.length > 0 && (
                    <div className="mt-20 text-center border-t border-white/10 pt-10">
                        <p className="text-gray-500 text-sm">
                            Showing {newsItems.length} {newsItems.length === 1 ? 'article' : 'articles'}
                            {feedInfo?.itemCount && feedInfo.itemCount > newsItems.length &&
                                ` of ${feedInfo.itemCount} total`
                            }
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default News;
